import type { ReactNode } from "react";
import { EnvironmentOutlined, MessageOutlined, PhoneOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { SeatableReservation } from "../../../../../api/reservations";
import { DiningTableIcon } from "../../../../../components/restaurant-schema/schemaIcons";
import { useReservationFormat } from "../../../../reservations/hooks/useReservationFormat";
import { formatClock } from "../../../../reservations/logic/dayTime";
import { distinctTablesOf } from "../../../../reservations/logic/seatBills";
import { reservationEndMs, reservationStartMs } from "../../../../reservations/logic/tableStatus";

/**
 * Резервацията във въпроса „Настани Иванови?" — карта-билет: горе часът и
 * колко трае, под перфорацията — гостите, телефонът, масите („Маси 2, 3,
 * 13") и районът с иконки, най-долу бележката (повод, алергии) в отделно каре. `compact` — на
 * телефон, над избора: часът и гостите в горната лента, без района.
 */
export const SeatReservationTicket = ({
  reservation,
  compact = false,
}: {
  reservation: SeatableReservation;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const tables = distinctTablesOf(reservation.tableNames);
  const regions = [...new Set(reservation.regionNames)].join(", ");
  const guests = t("reservations.counts.guests", { count: reservation.guests });

  return (
    <div className="overflow-hidden rounded-2xl border border-pos-line2 bg-pos-card">
      <div className={`flex items-center justify-between gap-3 bg-pos-ready-bg ${compact ? "px-3.5 py-2.5" : "px-4 py-3.5"}`}>
        <span className="flex min-w-0 items-baseline gap-1 font-pos-mono font-bold leading-none text-pos-ink">
          <span className={compact ? "text-[18px]" : "text-[24px]"}>{formatClock(reservationStartMs(reservation))}</span>
          <span className="text-[14px] text-pos-soft2">–{formatClock(reservationEndMs(reservation))}</span>
        </span>
        <span className="shrink-0 text-[12.5px] font-bold text-pos-accent-ink">
          {compact ? guests : format.duration(reservation.durationMinutes)}
        </span>
      </div>

      {/* Перфорацията на билета — прорезите са в цвета на колоната зад картата. */}
      <div className="relative h-0 border-t border-dashed border-pos-line3">
        <span className="absolute -left-2 -top-2 size-4 rounded-full border border-pos-line2 bg-pos-canvas" />
        <span className="absolute -right-2 -top-2 size-4 rounded-full border border-pos-line2 bg-pos-canvas" />
      </div>

      <div className={`flex flex-col ${compact ? "gap-2 px-3.5 py-3" : "gap-2.5 px-4 py-4"}`}>
        {!compact && <TicketRow icon={<TeamOutlined />}>{guests}</TicketRow>}
        {reservation.phone && (
          <TicketRow icon={<PhoneOutlined />}>
            <span className="font-pos-mono">{reservation.phone}</span>
          </TicketRow>
        )}
        {/* Масите — текст като другите редове, иконата е маса отстрани. */}
        <TicketRow icon={<DiningTableIcon className="size-4" strokeWidth={1.8} />}>
          {t("seatReservation.tables", { count: tables.length, tables: tables.join(", ") })}
        </TicketRow>
        {!compact && regions && <TicketRow icon={<EnvironmentOutlined />}>{regions}</TicketRow>}
        {reservation.note && (
          <div className="mt-1 flex items-start gap-2 rounded-xl bg-pos-marked px-3 py-2.5 text-[13px] font-semibold leading-[1.4] text-pos-accent-ink">
            {/* `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите. */}
            <MessageOutlined className="mt-[3px] shrink-0 !text-pos-accent" />
            <span className="min-w-0 break-words">{reservation.note}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/** Ред от билета: иконка в плочка + стойност. */
const TicketRow = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <div className="flex min-w-0 items-center gap-2.5 text-[14px] font-semibold text-pos-ink">
    <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-pos-canvas text-[13px] text-pos-soft2">
      {icon}
    </span>
    <span className="min-w-0 break-words">{children}</span>
  </div>
);
